import { useMemo, useState } from 'react'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import { MainContent, ModalContext, TopBar } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useMainNavigation from '@hooks/useMainNavigation'

import ArtworkInUseModal from '@modals/ArtworkInUseModal/ArtworkInUseModal'

import AssignTemplatePagePermissionsModal, { AssignTemplatePagePermissionsModalPayload } from '@modals/AssignTemplatePagePermissionsModal/AssignTemplatePagePermissionsModal'
import ManageEntityActivityModal from '@modals/ManageEntityActivityModal/ManageEntityActivityModal'
import ViewTemplatePageDetailsModal from '@modals/ViewTemplatePageDetailsModal'


import DashboardSearch from '@components/DashboardSearch'
import LoggedInUser from '@components/LoggedInUser'
import MainNavigation from '@sections/Admin/components/MainNavigation'

import PageContent from './components/PageContent'

import type { TemplatePageModel } from '@domain-group/realhub-backend-api-types'
import BulkPermissionsModal from '@modals/BulkPermissionsModal/BulkPermissionsModal'

type ArtworkInUseModalPayload = {
  templatePage?: TemplatePageModel,
}
type BulkPermissionsModalPayload = { selectedTemplateIds: number[] }
type ManageEntityActivityModalPayload = {
  conversationTitle?: string,
  subjectId?: number,
  subjectType?: string,
  title?: string,
  titleSecondLine?: string,
}

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showArtworkInUseModal: (payload: ArtworkInUseModalPayload) => void,
    showManageEntityActivityModal: (payload: ManageEntityActivityModalPayload) => void,
    showAssignTemplatePagePermissionsModal: (payload: AssignTemplatePagePermissionsModalPayload) => void,
    showViewTemplatePageDetailsModal: (payload: object) => void,
    showBulkPermissionsModal: (payload: BulkPermissionsModalPayload) => void,
  },
}

const defaultState = {
  showArtworkInUseModal: false,
  showManageEntityActivityModal: false,
  showAssignTemplatePagePermissionsModal: false,
  showViewTemplatePageDetailsModal: false,
  showBulkPermissionsModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showArtworkInUseModal,
    showAssignTemplatePagePermissionsModal,
    showManageEntityActivityModal,
    showViewTemplatePageDetailsModal,
    showBulkPermissionsModal,
  } = state
  const [artworkInUseModalPayload, setArtworkInUseModalPayload] = useState<
    ArtworkInUseModalPayload | undefined
  >(undefined)
  const [manageEntityActivityModalPayload, setManageEntityActivityModalPayload] = useState<
    ManageEntityActivityModalPayload | null
  >(null)

  const [assignTemplatePagePermissionsPayload, setAssignTemplatePagePermissionsPayload] = useState<
    AssignTemplatePagePermissionsModalPayload | null
  >(null)
  
  const { callbacks: { toggleNavigation } } = useMainNavigation()
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([])
  const filtersPayload = useFilters({ persistFilters: true })
  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals()
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showArtworkInUseModal: (payload: ArtworkInUseModalPayload) => {
        setArtworkInUseModalPayload(payload)
        setState({ showArtworkInUseModal: true })
      },
      showManageEntityActivityModal: (payload: ManageEntityActivityModalPayload) => {
        setManageEntityActivityModalPayload(payload)
      },
      showAssignTemplatePagePermissionsModal: (payload: AssignTemplatePagePermissionsModalPayload) => {
        setAssignTemplatePagePermissionsPayload(payload)
      },
      showViewTemplatePageDetailsModal: (payload: object) => {
        setModalData('ViewTemplatePageDetailsModal', payload)
        setState({ showViewTemplatePageDetailsModal: true })
      },
      showBulkPermissionsModal: (payload: BulkPermissionsModalPayload) => {
        setModalData('BulkPermissionsModal', payload) // or setBulkPermissionsModalPayload(payload)
        setSelectedTemplateIds(payload?.selectedTemplateIds ?? [])
        setState({ showBulkPermissionsModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt]) // modalContext dependency required if layering modals

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <TopBar
          callbacks={{ toggleNavigation }}
          loggedInUserComponent={<LoggedInUser />}
          searchComponent={(
            <DashboardSearch
              searchEntities={['LayoutTemplate', 'TaskList', 'TemplatePage', 'TemplatePagePartial']}
            />
          )}
        />

        <MainNavigation />

        <MainContent offset={{ top: TopBar.topBarHeight }}>
          {assignTemplatePagePermissionsPayload === null && !showViewTemplatePageDetailsModal
          && <PageContent pageFilters={pageFilters} />}
        </MainContent>

        {assignTemplatePagePermissionsPayload !== null && (
          <AssignTemplatePagePermissionsModal
            callbacks={
              {
                bulkAssignTemplatePagePermissions: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
                closeModal: () => setAssignTemplatePagePermissionsPayload(null),
              }
            }
            showModal={true}
            payload={assignTemplatePagePermissionsPayload}
          />
        )}

        <ArtworkInUseModal
          callbacks={{
            closeModal: () => {
              setState({ showArtworkInUseModal: false })
              setArtworkInUseModalPayload(undefined)
            },
          }}
          payload={artworkInUseModalPayload}
          showModal={showArtworkInUseModal}
        />

        {manageEntityActivityModalPayload !== null && (
          <ManageEntityActivityModal
            callbacks={{closeModal: () => setManageEntityActivityModalPayload(null)}}
            payload={manageEntityActivityModalPayload}
            showModal={true}
          />
        )}

        <ViewTemplatePageDetailsModal
          callbacks={{
            closeModal: () => setState({ showViewTemplatePageDetailsModal: false }),
          }}
          requestOptions={{
            include_related_entity_owner: true,
            include_template_page_permission_ids: true,
            include_template_page_related_entities_where_subject: true,
          }}
          showModal={showViewTemplatePageDetailsModal}
        />
        {showBulkPermissionsModal && (
          <BulkPermissionsModal
            modalKey="BulkPermissionsModal"
            selectedTemplateIds={selectedTemplateIds}
            callbacks={{
              closeModal: () => {
                setState({ showBulkPermissionsModal: false })
                setSelectedTemplateIds([])
              },
              assignManually: ({ additionalTemplatePageIds, stepKey, templatePageId }) => {
                setState({ showBulkPermissionsModal: false })
                setSelectedTemplateIds([])
                setAssignTemplatePagePermissionsPayload({
                  additionalTemplatePageIds,
                  stepKey,
                  templatePage: { id: templatePageId },
                })
              },
            }}
            showModal
          />
        )}
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
